<template>
  <ca-modal
    :show.sync="show_copy"
    :titulo="modo_extender_tiempo_aceptacion ? 'Extender período de aceptación' : 'Finalizar período de aceptación'"
    :tipo="modo_extender_tiempo_aceptacion ? 'default' : 'warning'"
    contentClass="modal-modificar-tiempo-trial"
  >

    <!-- Header -->
    <div class="header-modificar-tiempo-trial">
      <header-label-tipo-acuerdo-comercial-sidesheet
        :acuerdo_comercial="acuerdo_comercial"
        :mostrar_texto_titulo="false"
      />
    </div>

    <!-- Detalle estado acuerdo comercial -->
    <detalle-estado-acuerdo-comercial :acuerdo_comercial="acuerdo_comercial"/>

    <!-- Modo: Extender período de aceptación -->
    <v-form v-if="modo_extender_tiempo_aceptacion" v-model="formulario_valido" ref="formulario_modificar_tiempo_trial" class="formulario-modificiar-tiempo-trial">
      <p>Ingresá la cantidad de días para extender el período de aceptación:</p>

      <v-text-field
        v-model="dias_a_extender"
        type="number"
        label="Cantidad de días"
        class="input-extender-trial"
        dense
        required
        autocomplete="off"
        :rules="[
          v => !!v || 'Obligatorio',
          v => v >= 1 || 'El mínimo es 1 día',
          v => v >= 1 && v <= 15 || 'El maximo es 15 días'
        ]"
      />

      <ca-chip
        v-if="formulario_valido"
        :item="{ label: getLabelFechaExpiracion }"
        contentClass="chip-custom chip-informativo mt-3"
        disabled
      >
        <div slot="izquierda">
          <v-icon class="icon-info">info</v-icon>
        </div>
      </ca-chip>

    </v-form>

    <!-- Modo: Finalizar período de aceptación -->
    <div v-else class="label">
      <div class="mt-6">
        Luego de ejecutar esta operación el consorcio pasará a <span class="texto-negrita">modo sólo lectura.</span>
      </div>

      <br>

      <div>¿Confirmás continuar con la operación?</div>
    </div>

    <!-- Botón cancelar -->
    <div slot="modal-botones-prev" class="contenedor-botonera">
      <v-btn @click="$emit('update:show', false)" text class="ca-btn-cancelar-modal">CANCELAR</v-btn>

      <v-btn :disabled="modo_extender_tiempo_aceptacion && !formulario_valido" @click="modo_extender_tiempo_aceptacion ? extenderTrial() : finalizarTrial()" class="ca-panel-buttonGuardar">
        {{ modo_extender_tiempo_aceptacion ? 'CONFIRMAR' : 'FINALIZAR PERÍODO' }}
      </v-btn>
    </div>

  </ca-modal>
</template>

<script>
import { AcuerdoComercial } from '@/models/AcuerdoComercial';
import { LAYOUT_MUTATION_MODAL_ERROR, LAYOUT_MUTATION_MOSTRAR_LOADING, LAYOUT_MUTATION_OCULTAR_LOADING, LAYOUT_MUTATION_SNACKBAR_EXITO } from '@/store/layout/layout.types';
import { APIAcuerdoComercial } from '../api/acuerdo-comercial.api';
import DetalleEstadoAcuerdoComercial from './DetalleEstadoAcuerdoComercial.vue';
import HeaderLabelTipoAcuerdoComercialSidesheet from './HeaderLabelTipoAcuerdoComercialSidesheet.vue';
import {AcuerdoComercialTrial} from '../models/AcuerdoComercialTrial';
import { formatFecha } from '@/components/vue_filters';
import { add, differenceInDays } from 'date-fns';
import { cloneDeep } from 'lodash';

/**
 * Componente que permite modificar el tiempo de aceptación de un acuerdo comercial, tanto para extenderlo como para finalizarlo
 * Esta dualidad de funcionalidades se maneja a través del flag del modo de visualización y nos permite reutilizar el mismo componente para ambas acciones
 * Dependiendo del modo que este activo se mostrará el modal asociado y se ejecutará la acción correspondiente
 */
export default ({
  name: 'ModalModificarTiempoTrialAcuerdoComercial',
  components: {
    DetalleEstadoAcuerdoComercial,
    HeaderLabelTipoAcuerdoComercialSidesheet
  },
  props: {
    show: { type: Boolean, default: false },
    acuerdo_comercial: { type: AcuerdoComercial, default: null },
    modo_extender_tiempo_aceptacion: { type: Boolean, default: true } // Modo de visualización del modal, por defecto es extender tiempo de aceptación, caso contrario seria finalizar periodo de aceptación
  },
  data: () => ({
    show_copy: false,
    acuerdo_comercial_copy: null,

    dias_a_extender: null,
    fecha_nueva_actualizada: null, // Variable para almacenar la última fecha de expiración actualizada.

    formulario_valido: false
  }),
  computed: {
    /**
     * Devuelve el texto que se mostrará en el chip de la fecha de expiración.
     * Concatena el texto "Fecha de expiración" con la fecha calculada en formato dd/mm/yyyy.
     */
    getLabelFechaExpiracion() {
      return this.dias_a_extender && this.acuerdo_comercial ? ('Próxima fecha de expiración: ' + formatFecha(this.calcularNuevaFechaExpiracion(), 'dd/MM/yyyy')) : null;
    }
  },
  watch: {
    show(new_value) {
      this.show_copy = new_value;
    }
  },
  methods: {
    /**
     * Calcula la nueva fecha de expiración a partir de la fecha actual o de la fecha de fin del trial.
     *  - Si el acuerdo ya está expirado, se calcula a partir de la fecha actual.
     *  - Si el acuerdo no está expirado, se calcula a partir de la fecha de fin del trial.
     */
    calcularNuevaFechaExpiracion() {
      this.fecha_nueva_actualizada = this.acuerdo_comercial.expirada ?
        add(new Date(), { days: this.dias_a_extender }) :
        add(new Date(this.acuerdo_comercial.propuesta_consorcio.fecha_fin_trial+'T00:00:00'),  { days: this.dias_a_extender });

      return this.fecha_nueva_actualizada;
    },

    /**
     * Valida si el formulario es válido y realiza la llamada a la API para extender el tiempo del trial.
     * Si la llamada es exitosa, se emite el evento 'exito-acuerdo:extender-trial' con el acuerdo comercial actualizado.
     */
    extenderTrial() {
      if (this.$refs.formulario_modificar_tiempo_trial.validate()) {
        this.$store.commit(LAYOUT_MUTATION_MOSTRAR_LOADING);

        let acuerdo_comercial_trial = new AcuerdoComercialTrial(this.dias_a_extender);
        let link_extender = this.acuerdo_comercial.getApiLinkHref('extender_trial');

        APIAcuerdoComercial.extenderTiempoTrial(link_extender, acuerdo_comercial_trial)
          .then(() => {
            // Se clona el acuerdo comercial y se actualiza los días restantes y la fecha de fin del trial con la fecha calculada en método externo.
            this.acuerdo_comercial_copy = cloneDeep(this.acuerdo_comercial);

            const hoy = (new Date()).setHours(0, 0, 0, 0);
            this.acuerdo_comercial_copy.propuesta_consorcio.dias_restantes = differenceInDays(this.fecha_nueva_actualizada, hoy);
            this.acuerdo_comercial_copy.propuesta_consorcio.fecha_fin_trial = formatFecha(this.fecha_nueva_actualizada, 'yyyy-MM-dd');
            this.acuerdo_comercial_copy.fecha_cambio_estado_actual = formatFecha(new Date(), 'yyyy-MM-dd hh:mm');

            this.$emit('exito-acuerdo:accion-trial', this.acuerdo_comercial_copy);

            this.$store.commit(LAYOUT_MUTATION_SNACKBAR_EXITO, 'Se actualizó el período de aceptación del acuerdo');
          })
          .catch((err) => {
            this.$store.commit(LAYOUT_MUTATION_MODAL_ERROR, {
              texto: err.detail
            });
          })
          .finally(() => {
            this.$emit('update:show', false); // Show en sync, comunica al padre para cerrar modal 
            this.$store.commit(LAYOUT_MUTATION_OCULTAR_LOADING);
          });
      }
      else {
        return false;
      }
    },

    /**
     * Metodo encargado de finalizar el período de aceptación del acuerdo comercial llamando a la API
     * Además actualiza variables del acuerdo comercial y emite evento para notificar al padre que se finalizó el período de aceptación
     */
    finalizarTrial() {
      this.$store.commit(LAYOUT_MUTATION_MOSTRAR_LOADING);

      let acuerdo_comercial_trial_finalizado = new AcuerdoComercialTrial();
      let link_finalizar_periodo = this.acuerdo_comercial.getApiLinkHref('finalizar_trial');

      APIAcuerdoComercial.finalizarTiempoTrial(link_finalizar_periodo, acuerdo_comercial_trial_finalizado)
        .then(() => {
          this.acuerdo_comercial_copy = cloneDeep(this.acuerdo_comercial);

          const hoy = (new Date()).setHours(0, 0, 0, 0);
          this.acuerdo_comercial_copy.propuesta_consorcio.dias_restantes = null;
          this.acuerdo_comercial_copy.propuesta_consorcio.fecha_fin_trial = hoy;
          this.acuerdo_comercial_copy.fecha_cambio_estado_actual = formatFecha(new Date(), 'yyyy-MM-dd hh:mm');

          this.$emit('exito-acuerdo:accion-trial', this.acuerdo_comercial_copy);

          this.$store.commit(LAYOUT_MUTATION_SNACKBAR_EXITO, 'Se finalizó el período de aceptación del acuerdo');
        })
        .catch((err) => {
          this.$store.commit(LAYOUT_MUTATION_MODAL_ERROR, {
            texto: err.detail
          });
        })
        .finally(() => {
          this.$emit('update:show', false);
          this.$store.commit(LAYOUT_MUTATION_OCULTAR_LOADING);
        });
    }
  }
});
</script>
<style lang="scss">

.modal-modificar-tiempo-trial {
  .modal-content {
    padding: 12px 24px 24px 24px !important;
  }

  .v-card__title {
    font-weight: 400 !important;
  }

  .header-modificar-tiempo-trial {
    margin-bottom: 10px;
  }

  .formulario-modificiar-tiempo-trial {
    margin-top: 24px;
    font-size: 15px;
    color: color(secondary);
  }

  .input-extender-trial {
    width: 140px;
  }

  .chip-informativo {
    background-color: color(fondo-claro) !important;
    border: 1px solid color(celeste-claro);
  }

  .texto-negrita {
    font-weight: 700;
  }

  .label {
    color: color(secondary);
  }

  .contenedor-botonera {
    display: flex;
    justify-content: space-between;
    width: 100%;
  }

  // Estilos generales de los chips
  .chip-custom {
    font-size: 15px;
    margin: 0;
    padding: 8px;
    height: auto !important;

    .v-chip__content {
      display:flex;
      align-items: center;
      padding: 0;
      margin-left: 4px;
      background-color: inherit;
    }

    .ca-chip-label {
      font-size: 14px;
      white-space: break-spaces;
      color: color(dark-secund);
    }

    .ca-chip-izquierda{

      .icon-info {
        color: color(celeste);
      }

      .icon-warning {
        color: color(mango);
      }
    }
  }
}
</style>
